import router, { setRoutes } from "../router";
import store, { loadModules, preloadStores } from "shared/store";
import { RouteConfig } from "vue-router";
import Vue, { VNode } from "vue";
import {
  BootstrapConfig,
  MenuItem,
  ModuleManifest,
  VuePlugins,
  ModuleManifestArray
} from "./types";
import { ModuleTree } from "vuex";
import App from "shared/global/layouts/App.vue";
import vuetifyLoader from "shared/bootstrap/vuetify";
import VeeValidate from "shared/bootstrap/vee-validate";
import i18nSetup, { KeyedMessages, loadLocalisations } from "shared/i18n";
import { setupClient, getServerSideConfig } from "shared/api";
import auth from "shared/store/auth";
import global from "shared/store/global";
import snackbar from "shared/store/snackbar";
import { installPlugins } from "./vuePlugins";
import { Widget, registerWidgets } from "shared/widgets";

Vue.config.productionTip = false;

interface Settings {
  routes: RouteConfig[];
  storeModules: ModuleTree<{}>;
  menuItems: MenuItem[];
  vuePlugins: VuePlugins[];
  widgets: Widget[];
  localisations: KeyedMessages[];
}

const loadBootstrapConfigDefaults = (
  config: Partial<BootstrapConfig>
): BootstrapConfig => {
  return {
    ...{
      title: "",
      modules: [],
      vuetify: {},
      axios: {},
      continuousAuthCheck: true,
      customLocalisation: {},
      branding: false,
      loginBackground: false
    },
    ...config
  };
};

const loadServerConfig = async (config: BootstrapConfig): Promise<void> => {
  const server = await getServerSideConfig();

  if (server.title !== undefined) {
    config.title = server.title;
  }
  if (server.theme !== undefined) {
    config.vuetify.theme = { ...config.vuetify.theme, ...server.theme };
  }
  config.branding = server.branding;
  config.loginBackground = server.login_background;
};

const parseModules = (modules: ModuleManifestArray): Settings => {
  const res: Settings = {
    routes: [],
    storeModules: {},
    menuItems: [],
    vuePlugins: [],
    widgets: [],
    localisations: []
  };

  modules.forEach((m): void => {
    let module: ModuleManifest;
    if (typeof m === "function") {
      module = m();
    } else {
      module = m;
    }

    if (module.routes !== undefined) res.routes.push(...module.routes);
    if (module.storeModules !== undefined)
      res.storeModules = { ...res.storeModules, ...module.storeModules };
    if (module.menuItems !== undefined) res.menuItems.push(...module.menuItems);

    if (module.vuePlugins !== undefined) {
      module.vuePlugins.forEach((plugin): void => {
        if (!res.vuePlugins.includes(plugin)) res.vuePlugins.push(plugin);
      });
    }

    if (module.widgets !== undefined) res.widgets.push(...module.widgets);

    if (module.localisation !== undefined)
      res.localisations.push(module.localisation);
  });

  return res;
};

const loadBaseSetting = (settings: Settings): void => {
  settings.storeModules = { auth, global, snackbar, ...settings.storeModules };
};

export default async (config?: Partial<BootstrapConfig>): Promise<void> => {
  if (config === undefined) config = {};
  const filledConfig = await loadBootstrapConfigDefaults(config);
  setupClient(filledConfig.axios);
  await loadServerConfig(filledConfig);

  VeeValidate();
  vuetifyLoader(filledConfig.vuetify);

  const settings = parseModules(filledConfig.modules);
  loadBaseSetting(settings);

  installPlugins(settings.vuePlugins);
  setRoutes(settings.routes);
  loadModules(settings.storeModules);
  registerWidgets(settings.widgets);
  loadLocalisations(settings.localisations);

  preloadStores(filledConfig);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props: { [key: string]: any } = {
    title: filledConfig.title,
    menuItems: settings.menuItems,
    authCheck: filledConfig.continuousAuthCheck,
    branding: filledConfig.branding
  };

  if (filledConfig.customToolbar !== undefined) {
    props.customToolbar = filledConfig.customToolbar;
  }

  const i18n = await i18nSetup(filledConfig.customLocalisation);

  new Vue({
    router,
    store,
    i18n,
    render: (h): VNode =>
      h(App, {
        props
      })
  }).$mount("#app");
};
